// "New code" dialog for a PadSplit room row: auto-generates an editable code
// (respecting device_settings codeDigits), creates it on the mapped physical
// lock(s) via the existing hardened ttlock/seam access-code routes, then
// optionally pushes it to PadSplit. A room's rotation can span TWO locks —
// its own room lock and its row on the common lock — so both appear as
// checkboxes when both are mapped.
//
// Dismissal is gated on the in-flight mutation per the project convention
// (docs/dialog-dismissal-convention.md): onOpenChange + all three
// DialogContent dismissal handlers no-op while pending.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Dices } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import type { LockCodeComparisonRow, LockComparisonGroup } from "./padsplit-sync-types";

export interface CreateCodeTarget {
  /** The PadSplit lock group whose row was clicked. */
  group: LockComparisonGroup;
  row: LockCodeComparisonRow;
  /** Other PadSplit locks carrying the same room's row (e.g. the common lock). */
  siblingGroups: LockComparisonGroup[];
}

function generateCode(digits: number): string {
  const len = Math.min(Math.max(digits, 4), 8);
  let code = "";
  for (let i = 0; i < len; i += 1) code += Math.floor(Math.random() * 10);
  // Avoid a leading zero — some lock firmwares reject it.
  return code[0] === "0" ? `1${code.slice(1)}` : code;
}

async function createCodeOnDevice(opts: {
  deviceId: string;
  provider: string | null;
  connectionId: number | null;
  code: string;
  label: string;
}): Promise<void> {
  if (opts.provider === "seam") {
    await apiRequest("POST", "/api/app/devices/seam/access-codes", {
      connectionId: opts.connectionId,
      deviceId: opts.deviceId,
      name: opts.label,
      code: opts.code,
    });
    return;
  }
  // TTLock/Sifely deviceId format: ttlock-<connectionId>-<ttlockLockId>
  const parts = String(opts.deviceId ?? "").split("-");
  const lockId = Number(parts[parts.length - 1]);
  await apiRequest("POST", "/api/app/devices/ttlock/access-codes", {
    connectionId: opts.connectionId,
    lockId,
    keyboardPwd: opts.code,
    keyboardPwdName: opts.label,
  });
}

export interface CreateLockCodeDialogProps {
  target: CreateCodeTarget | null;
  onClose: () => void;
  propertyId: number;
  codeDigits: number;
  /** Query keys to invalidate after a successful run. */
  invalidateKeys: unknown[][];
}

export function CreateLockCodeDialog({
  target,
  onClose,
  propertyId,
  codeDigits,
  invalidateKeys,
}: CreateLockCodeDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [pushToPadsplit, setPushToPadsplit] = useState(true);
  const [checkedDevices, setCheckedDevices] = useState<Record<string, boolean>>({});

  const roomLabel = target?.row.roomLabel ?? "";

  // Every mapped physical lock this room's code should land on (deduped).
  const deviceTargets = useMemo(() => {
    if (!target) return [];
    const groups = [target.group, ...target.siblingGroups];
    const seen = new Set<string>();
    const out: Array<{
      deviceId: string;
      provider: string | null;
      connectionId: number | null;
      displayName: string;
      lockName: string;
    }> = [];
    for (const group of groups) {
      const mapping = group.mapping;
      if (!mapping || seen.has(mapping.deviceId)) continue;
      seen.add(mapping.deviceId);
      out.push({
        deviceId: mapping.deviceId,
        provider: mapping.provider,
        connectionId: mapping.connectionId,
        displayName: mapping.displayName ?? mapping.deviceId,
        lockName: group.lockName ?? group.padsplitLockKey,
      });
    }
    return out;
  }, [target]);

  // The PadSplit locks whose lightbox row gets the pushed code.
  const pushTargets = useMemo(() => {
    if (!target) return [];
    return [target.group, ...target.siblingGroups];
  }, [target]);

  useEffect(() => {
    if (target) {
      setCode(generateCode(codeDigits));
      setPushToPadsplit(true);
      setCheckedDevices(Object.fromEntries(deviceTargets.map((d) => [d.deviceId, true])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const runMutation = useMutation({
    mutationFn: async () => {
      if (!target) return;
      const trimmed = code.trim();
      if (!/^[0-9]{4,8}$/.test(trimmed)) {
        throw new Error("Enter a 4–8 digit numeric code.");
      }
      const label = `${roomLabel}${target.row.assignedTo ? ` - ${target.row.assignedTo}` : ""}`.trim() || "PadPulse";
      // 1. Create on each checked physical lock (sequential — lock providers
      //    throttle; a failure aborts before any PadSplit push happens).
      for (const device of deviceTargets) {
        if (!checkedDevices[device.deviceId]) continue;
        await createCodeOnDevice({
          deviceId: device.deviceId,
          provider: device.provider,
          connectionId: device.connectionId,
          code: trimmed,
          label,
        });
      }
      // 2. Queue the PadSplit pushes (one per PadSplit lock carrying the row).
      if (pushToPadsplit) {
        for (const group of pushTargets) {
          await apiRequest("POST", "/api/app/devices/padsplit-locks/pushes", {
            propertyId,
            padsplitLockKey: group.padsplitLockKey,
            padsplitLockName: group.lockName,
            roomLabel,
            code: trimmed,
            codeLabel: label,
            deviceId: group.mapping?.deviceId ?? null,
          });
        }
      }
    },
    onSuccess: () => {
      for (const key of invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      toast({
        title: "Code created",
        description: pushToPadsplit
          ? "The code is on the lock and queued to push to PadSplit."
          : "The code is on the lock. PadSplit was not updated.",
      });
      onClose();
    },
    onError: (err: unknown) => {
      toast({
        title: "Couldn't create the code",
        description: parseErrorMessage(err, "Something went wrong creating the code."),
        variant: "destructive",
      });
    },
  });

  const inFlight = runMutation.isPending;
  const anyDeviceChecked = deviceTargets.some((d) => checkedDevices[d.deviceId]);

  return (
    <Dialog
      open={target != null}
      onOpenChange={(next) => {
        if (!next && inFlight) return;
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => {
          if (inFlight) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (inFlight) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (inFlight) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>New code for {roomLabel || "this room"}</DialogTitle>
          <DialogDescription>
            Creates the code on the physical lock(s) first, then pushes it to PadSplit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="new-lock-code" className="text-xs font-semibold">Code</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="new-lock-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                className="font-mono text-lg font-bold tracking-widest"
                inputMode="numeric"
                data-testid="input-new-lock-code"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setCode(generateCode(codeDigits))}
                disabled={inFlight}
                data-testid="button-regenerate-code"
              >
                <Dices className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Auto-generated from your device settings ({codeDigits} digits). Edit freely before creating.
            </p>
          </div>

          <div className="divide-y border-t">
            {deviceTargets.map((device) => (
              <label key={device.deviceId} className="flex items-start gap-2.5 py-2.5 text-sm cursor-pointer">
                <Checkbox
                  checked={!!checkedDevices[device.deviceId]}
                  onCheckedChange={(checked) =>
                    setCheckedDevices((prev) => ({ ...prev, [device.deviceId]: checked === true }))
                  }
                  disabled={inFlight}
                  data-testid={`checkbox-device-${device.deviceId}`}
                />
                <span>
                  <span className="font-medium">Create on {device.displayName}</span>
                  {device.provider && <span className="text-muted-foreground"> ({device.provider})</span>}
                  <span className="block text-xs text-muted-foreground">
                    {roomLabel}'s code on the {device.lockName} lock
                  </span>
                </span>
              </label>
            ))}
            {deviceTargets.length === 0 && (
              <p className="py-2.5 text-sm text-muted-foreground" data-testid="text-no-mapped-devices">
                No mapped physical lock — the code will only be pushed to PadSplit.
              </p>
            )}
            <label className="flex items-start gap-2.5 py-2.5 text-sm cursor-pointer">
              <Checkbox
                checked={pushToPadsplit}
                onCheckedChange={(checked) => setPushToPadsplit(checked === true)}
                disabled={inFlight}
                data-testid="checkbox-push-to-padsplit"
              />
              <span>
                <span className="font-medium">Push to PadSplit after creating</span>
                <span className="block text-xs text-muted-foreground">
                  Sets {roomLabel}'s access code on{" "}
                  {pushTargets.map((g) => g.lockName ?? g.padsplitLockKey).join(" and ")}
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={inFlight} data-testid="button-cancel-new-code">
            Cancel
          </Button>
          <Button
            onClick={() => runMutation.mutate()}
            disabled={inFlight || (!anyDeviceChecked && !pushToPadsplit)}
            data-testid="button-create-and-push"
          >
            {inFlight ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
            {pushToPadsplit ? "Create & Push" : "Create code"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
